import React, { useState, useEffect } from 'react';
import { blogAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import { useLanguage } from '../../context/LanguageContext';
import Pagination from '../../components/Pagination';
import { useNavigate } from 'react-router-dom';
import './Admin.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { success, error } = useToast();
  const { formatDate } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await blogAPI.adminGetPosts();
      setPosts(Array.isArray(response.data) ? response.data : response.data?.results || []);
    } catch (err) {
      error('無法載入文章列表');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId, postTitle) => {
    if (!window.confirm(`確定要刪除文章「${postTitle}」？`)) return;

    try {
      await blogAPI.adminDeletePost(postId);
      success('文章已刪除');
      fetchPosts();
    } catch (err) {
      error('刪除失敗');
      console.error('Error deleting post:', err);
    }
  };

  const handleToggleFeatured = async (post) => {
    try {
      await blogAPI.adminPatchPost(post.id, { is_featured: !post.is_featured });
      success(post.is_featured ? '已取消精選' : '已設為精選');
      fetchPosts();
    } catch (err) {
      error('更新失敗');
      console.error('Error toggling featured:', err);
    }
  };

  const handleTogglePublished = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      await blogAPI.adminPatchPost(post.id, { status: newStatus });
      success(newStatus === 'published' ? '已發布' : '已改為草稿');
      fetchPosts();
    } catch (err) {
      error('更新失敗');
      console.error('Error toggling published:', err);
    }
  };

  const categories = [...new Set(posts.map((p) => p.category).filter(Boolean))];

  const filteredPosts = posts.filter((post) => {
    const matchesSearch = !search ||
      post.title?.toLowerCase().includes(search.toLowerCase()) ||
      post.summary?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || post.category === categoryFilter;
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'published' && post.status === 'published') ||
      (statusFilter === 'draft' && post.status === 'draft');
    return matchesSearch && matchesCategory && matchesStatus;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, statusFilter]);

  const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <LoadingPage message="載入文章資料中..." />;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>最新消息管理</h1>
        <div className="admin-actions">
          <input
            type="text"
            placeholder="搜尋文章標題、內容..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button className="btn btn-primary" onClick={() => navigate('/admin/blog/new')}>
            新增文章
          </button>
        </div>
      </div>

      <div className="filter-tabs">
        <button className={statusFilter === 'all' ? 'active' : ''} onClick={() => setStatusFilter('all')}>
          全部 ({posts.length})
        </button>
        <button className={statusFilter === 'published' ? 'active' : ''} onClick={() => setStatusFilter('published')}>
          已發布 ({posts.filter((p) => p.status === 'published').length})
        </button>
        <button className={statusFilter === 'draft' ? 'active' : ''} onClick={() => setStatusFilter('draft')}>
          草稿 ({posts.filter((p) => p.status === 'draft').length})
        </button>
      </div>

      {categories.length > 0 && (
        <div className="filter-tabs secondary">
          <button className={categoryFilter === 'all' ? 'active' : ''} onClick={() => setCategoryFilter('all')}>
            所有分類
          </button>
          {categories.map((cat) => (
            <button key={cat} className={categoryFilter === cat ? 'active' : ''} onClick={() => setCategoryFilter(cat)}>
              {cat}
            </button>
          ))}
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>標題</th>
              <th>分類</th>
              <th>發布日期</th>
              <th>瀏覽次數</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredPosts.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                  沒有符合條件的文章
                </td>
              </tr>
            ) : (
              paginatedPosts.map((post) => (
                <tr key={post.id}>
                  <td>
                    <strong>{post.title}</strong>
                    {post.is_featured && <span className="badge badge-star">★ 精選</span>}
                  </td>
                  <td>{post.category || '-'}</td>
                  <td>
                    {post.published_date
                      ? formatDate(post.published_date)
                      : '-'}
                  </td>
                  <td>{post.views_count || 0}</td>
                  <td>
                    <span className={`status-badge ${post.status === 'published' ? 'status-published' : 'status-draft'}`}>
                      {post.status === 'published' ? '已發布' : '草稿'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleTogglePublished(post)}
                        className={`btn btn-sm ${post.status === 'published' ? 'btn-warning' : 'btn-success'}`}
                      >
                        {post.status === 'published' ? '改為草稿' : '發布'}
                      </button>
                      <button
                        onClick={() => handleToggleFeatured(post)}
                        className={`btn btn-sm ${post.is_featured ? 'btn-warning' : 'btn-outline'}`}
                      >
                        {post.is_featured ? '取消精選' : '精選'}
                      </button>
                      <button onClick={() => navigate(`/blog/${post.id}`)} className="btn btn-sm btn-secondary">
                        查看
                      </button>
                      <button onClick={() => navigate(`/admin/blog/${post.id}/edit`)} className="btn btn-sm btn-primary">
                        編輯
                      </button>
                      <button onClick={() => handleDelete(post.id, post.title)} className="btn btn-sm btn-danger">
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredPosts.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          totalItems={filteredPosts.length}
        />
      )}
    </div>
  );
};

export default Blog;
